import { Content, Group, Item, ItemText, Label, Portal, Root, ScrollDownButton, ScrollUpButton, Icon as SelectIcon, Separator, Trigger, Value, Viewport } from '@radix-ui/react-select';
import React, { ComponentPropsWithoutRef, ElementRef, forwardRef } from 'react';
import classNames from '@/lib/classNames';
import { Icon } from '../../global/Icons';

const Select = Root;

const SelectGroup = Group;

const SelectValue = Value;

const SelectTrigger = forwardRef<ElementRef<typeof Trigger>, ComponentPropsWithoutRef<typeof Trigger>>(({ className, children, ...props }, ref) => (
    <Trigger
        ref={ref}
        className={classNames(
            'flex w-full h-full rounded-md items-center justify-between gap-2 bg-white lg:text-[0.95vw] xl:text-sm 2xl:text-base text-primary focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap',
            className
        )}
        {...props}
    >
        {children}
        <SelectIcon asChild>
            <Icon id="chevron-down" className="h-5 w-5" aria-label="" />
        </SelectIcon>
    </Trigger>
));
SelectTrigger.displayName = Trigger.displayName;

const SelectContent = forwardRef<ElementRef<typeof Content>, ComponentPropsWithoutRef<typeof Content>>(({ className, children, position = 'popper', ...props }, ref) => (
    <Portal>
        <Content
            ref={ref}
            className={classNames(
                'overflow-hidden bg-white rounded-md border border-primary min-w-[8rem] shadow-md animate-in fade-in-80 z-50',
                position === 'popper' && 'translate-y-1',
                className
            )}
            position={position}
            {...props}
        >
            <ScrollUpButton className="flex items-center justify-center h-[25px] bg-white text-primary cursor-default">
                <Icon id="chevron-up" className="h-4 w-4" />
            </ScrollUpButton>
            <Viewport className={classNames('p-1', position === 'popper' && 'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]')}>{children}</Viewport>
            <ScrollDownButton className="flex items-center justify-center h-[25px] bg-white text-primary cursor-default">
                <Icon id="chevron-down" className="h-4 w-4" aria-label="down" />
            </ScrollDownButton>
        </Content>
    </Portal>
));
SelectContent.displayName = Content.displayName;

const SelectLabel = forwardRef<ElementRef<typeof Label>, ComponentPropsWithoutRef<typeof Label>>(({ className, ...props }, ref) => (
    <Label ref={ref} className={classNames('py-1.5 pl-8 pr-2 text-sm font-semibold', className)} {...props} />
));
SelectLabel.displayName = Label.displayName;

const SelectItem = forwardRef<ElementRef<typeof Item>, ComponentPropsWithoutRef<typeof Item>>(({ className, children, ...props }, ref) => (
    <Item
        ref={ref}
        className={classNames(
            'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-2 text-sm outline-none focus:bg-primary focus:text-white data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
            className
        )}
        {...props}
    >
        <ItemText>{children}</ItemText>
    </Item>
));
SelectItem.displayName = Item.displayName;

const SelectSeparator = forwardRef<ElementRef<typeof Separator>, ComponentPropsWithoutRef<typeof Separator>>(({ className, ...props }, ref) => (
    <Separator ref={ref} className={classNames('h-px bg-primary/20 w-full', className)} {...props} />
));
SelectSeparator.displayName = Separator.displayName;

export { Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator };
